/*
 * Carbisdale - CoreConsole
 *
 * Facade fuer die Core Console.
 * Verbindet CoreModel, CoreView und CoreInput.
 */

import { CoreModel, ConsoleEntry, DirtyLevel } from './CoreModel.js'
import { CoreView } from './CoreView.js'
import { CoreInput } from './CoreInput.js'
import { DummyAgentService } from '../agent/AgentServiceBase.js'
import { AUTO_APPLY_TOOLS } from '../agent/autoApplyToolMap.js'
import { ConsoleCommandDispatcher } from './ConsoleCommands.js'

/**
 * Liefert ein kompaktes Label fuer eine Block-Format-Konfiguration. Genutzt
 * im Review-Panel zum Anzeigen von Vorher/Nachher-Block-Aenderungen.
 *
 * @param {{headingLevel:number,listType:string|null,listLevel:number,blockquote:boolean}} block
 */
function describeBlock(block) {
    if (block.headingLevel > 0) return `Heading ${block.headingLevel}`
    if (block.listType === 'bullet') return `Bullet list (level ${block.listLevel || 0})`
    if (block.listType === 'numbered') return `Numbered list (level ${block.listLevel || 0})`
    if (block.blockquote) return 'Blockquote'
    return 'Plain'
}

// Tool-Call/Result-Formatter fuer den THINKING-Block. Kompakt, eine Zeile
// pro Aufruf. Input wird gestutzt, damit lange Inputs den Block nicht
// sprengen.
const TOOL_INPUT_PREVIEW_MAX = 200
function formatToolCall(tool, input) {
    let inputStr = ''
    if (input && typeof input === 'object' && Object.keys(input).length > 0) {
        try {
            inputStr = JSON.stringify(input)
        } catch {
            inputStr = '[unstringifiable]'
        }
        if (inputStr.length > TOOL_INPUT_PREVIEW_MAX) {
            inputStr = inputStr.slice(0, TOOL_INPUT_PREVIEW_MAX) + '…'
        }
        inputStr = ' ' + inputStr
    }
    return `\n→ ${tool}${inputStr}\n`
}
function formatToolResult(tool, summary) {
    return `← ${tool}: ${summary || 'ok'}\n`
}

/**
 * CoreConsole — Oeffentliche API der Console.
 */
export class CoreConsole {
    /**
     * @param {{x: number, y: number, width: number, height: number}} bounds - innerBounds des Core-Panes
     * @param {AgentService} [agentService] - AgentService-Instanz (Default: DummyAgentService)
     * @param {SoundManager} [soundManager] - optional, fuer AI-Ready/Aborted-Sounds
     * @param {AgentTransport} [mockTransport] - Optionaler Mock-Transport fuer /mock-Scenarios (Dev-only)
     */
    constructor(bounds, agentService = null, soundManager = null, mockTransport = null) {
        this.model = new CoreModel()
        this.view = new CoreView(this.model, bounds)
        this.input = new CoreInput(this.model, this.view, (text) => this._handleSend(text))

        this.agentService = agentService || new DummyAgentService()
        this._soundManager = soundManager
        this._streamRebuildPending = false

        // Optionaler Hook: neue ERROR-Zeile wurde geloggt (sichtbar im
        // aktuellen Projekt) — main.js oeffnet darauf die eingeklappte Console
        this.onErrorEntry = null

        // Mock-Transport fuer /mock-Scenarios (Phase 0.5 der Agent-Integration).
        // Wird nur gesetzt, wenn wir in einer Dev-Umgebung laufen — siehe main.js.
        this._mockTransport = mockTransport
        this._mockScenarioPending = null    // wenn gesetzt: naechstes submit startet das Scenario
        this._commandDispatcher = new ConsoleCommandDispatcher(this, mockTransport)

        // Delta-Review-Panel — ID des aktuellen REVIEW_PANEL-Entry (null wenn keiner aktiv).
        // ReviewController ruft setReviewPanel/clearReviewPanel ueber den panel-Slot an.
        this._reviewPanelEntryId = null

        // Optionaler ReviewController — wenn gesetzt, bekommt er propose_*
        // und onDone aus dem Mock-Transport-Pfad.
        this._reviewController = null

        // Optionaler Provider fuer cursorContext (wird vom Mock an Scenarios
        // weitergereicht, damit $cursor.paragraphId etc. resolvt werden).
        this._cursorContextProvider = null
        // Callback: setzt layout.isAgentBusy (fuer (!) Abort-Control).
        // Wird von main.js mit (busy) => ctx.layout.isAgentBusy = busy verdrahtet.
        this._onBusyChange = null

        // Pro-Projekt-State (Entries + nextId) und Busy-Flag
        this._currentProjectId = null
        this._projectStates = new Map()      // projectId → { entries, nextId }
        this._busyByProject = new Map()       // projectId → bool

        // Laufender Stream: gehalten, um beim Projektwechsel zu entscheiden ob abort
        this._activeStreamProjectId = null

        // Stream-Hooks-Bruecke fuer logToolStatus/logToolError. Solange ein Stream
        // laeuft, schreibt der ReviewController Tool-Apply-Status in den
        // aktiven THINKING-Block statt als separaten SYSTEM-Entry.
        this._activeStream = null

        // Scrollbar-Drag-State (analog zum Editor)
        this.isScrollbarDragging = false
        this._scrollbarDragStartY = 0
        this._scrollbarDragStartViewport = 0

        // EntriesProvider fuer Session-Rekonstruktion
        if (this.agentService.setEntriesProvider) {
            this.agentService.setEntriesProvider(() => this.model.getEntries())
        }

        this.bounds = { ...bounds }
    }

    /**
     * Initialisiert die Console (AgentService-Begruessungsnachricht).
     */
    async initialize() {
        const greeting = await this.agentService.initialize()
        if (greeting) {
            this.model.addEntry(greeting.type, greeting.text)
        }
        this.view.rebuildLines()
        this.view.scrollToBottom()
    }

    /**
     * Registriert den ReviewController. Wenn gesetzt, uebernimmt er
     * propose_*-Events und done aus dem Mock-Transport-Pfad.
     * @param {ReviewController} rc
     */
    setReviewController(rc) {
        this._reviewController = rc
    }

    /**
     * Registriert eine Funktion die bei jedem Mock-Send den aktuellen
     * cursorContext liefert (pane, paragraphId, version, text, ...).
     * Scenarios resolven darueber $cursor-Placeholder.
     * @param {function} fn - () => cursorContext
     */
    setCursorContextProvider(fn) {
        this._cursorContextProvider = fn
    }

    /**
     * Convenience-Wrapper fuer SYSTEM-Log — wird vom ReviewController
     * ueber den panel-Slot aufgerufen.
     *
     * Stream-aware: waehrend ein Stream laeuft, geht die Eintragung ueber
     * den Slot-Pfad (`_activeStream.addEntry`). Sonst kollidiert die ID-
     * Vergabe mit dem Stream-Hook — der nutzt `slot.nextId++` und syncs
     * `model.nextId = slot.nextId` nach jeder Eintragung; ein direkter
     * `model.addEntry` dazwischen erhoeht `model.nextId` ohne `slot.nextId`,
     * sodass der naechste Hook-Eintrag die schon vergebene ID nochmal
     * vergibt und `appendToEntry` in den falschen Entry schreibt (Lesson 81-
     * Reprise: erstes Token der Folge-Antwort landete im SYSTEM-Entry).
     * @param {string} text
     */
    logSystem(text) {
        if (this._activeStream?.addEntry) {
            this._activeStream.addEntry('SYSTEM', text)
            this._activeStream.scheduleRebuild?.()
            return
        }
        this.log(text, 'SYSTEM')
    }

    /**
     * Convenience-Wrapper fuer ERROR-Log — wird vom ReviewController
     * ueber den panel-Slot aufgerufen. Selber Stream-aware-Pfad wie logSystem.
     * @param {string} text
     */
    logError(text) {
        if (this._activeStream?.addEntry) {
            this._activeStream.addEntry('ERROR', text)
            this._activeStream.scheduleRebuild?.()
            return
        }
        this.log(text, 'ERROR')
    }

    /**
     * Tool-Status-Log fuer den ReviewController: waehrend ein Stream laeuft
     * landet die Meldung im aktiven THINKING-Block (gehoert logisch zum
     * Tool-Lifecycle und steht damit visuell vor der finalen AI-Antwort).
     * Ohne aktiven Stream Fallback auf SYSTEM-Entry am Ende der Console.
     *
     * Motiviert durch Boris' Test: 'Applied background rewrite directly.'
     * wurde mitten in den AI-Antworttext eingeschoben — der User sah einen
     * SYS-Block zwischen zwei Saetzen seiner eigentlichen Antwort.
     * @param {string} text
     */
    logToolStatus(text) {
        if (this._activeStream?.appendToThinking) {
            this._activeStream.appendToThinking('\n[' + text + ']\n')
        } else {
            this.log(text, 'SYSTEM')
        }
    }

    /**
     * Tool-Error-Log — analog zu logToolStatus, aber Fallback ist ERROR-Entry.
     * @param {string} text
     */
    logToolError(text) {
        if (this._activeStream?.appendToThinking) {
            this._activeStream.appendToThinking('\n[ERROR: ' + text + ']\n')
        } else {
            this.log(text, 'ERROR')
        }
    }

    /**
     * Zeigt (oder aktualisiert) das Review-Steuerungs-Panel als temporaeren
     * Entry am Ende der Console. Wird vom ReviewController bei jedem
     * Navigations- oder Toggle-Schritt aufgerufen. Englischer Text laut
     * Spec-Korrektur (§7).
     *
     * @param {{index, total, resolvedCount, type, reason, activeView}} info
     */
    setReviewPanel(info) {
        const text = this._formatReviewPanelText(info)
        if (this._reviewPanelEntryId != null) {
            // Existing panel aktualisieren — text komplett ersetzen statt anhaengen
            const entry = this.model.getEntries().find(e => e.id === this._reviewPanelEntryId)
            if (entry) {
                entry.text = text
                this.model._markDirty()
            } else {
                // Entry war weg — neu anlegen
                const newEntry = this.model.addEntry('REVIEW_PANEL', text)
                this._reviewPanelEntryId = newEntry.id
            }
        } else {
            const newEntry = this.model.addEntry('REVIEW_PANEL', text)
            this._reviewPanelEntryId = newEntry.id
            // Eingabe sperren waehrend Review — Input-Zeile wird so auch
            // im Cursor-Handling gemieden (isLocked()).
            this.model.lock()
        }
        this.view.rebuildLines()
        this.view.scrollToBottom()
    }

    /** Entfernt das Review-Panel, gibt Input frei. */
    clearReviewPanel() {
        if (this._reviewPanelEntryId == null) return
        this.model.removeEntry(this._reviewPanelEntryId)
        this._reviewPanelEntryId = null
        if (this.model.isLocked()) this.model.unlock()
        this.view.rebuildLines()
        this.view.scrollToBottom()
    }

    _formatReviewPanelText({ index, total, resolvedCount, type, reason, activeView, blockDelta }) {
        const kind = type === 'rewrite' ? 'Rewrite'
            : type === 'insert' ? 'Insert'
            : type === 'delete' ? 'Delete'
            : 'Review'
        const viewLabel = activeView === 'original' ? 'Original' : 'Proposed'
        const progress = `${(resolvedCount || 0)}/${total || 0} resolved`
        const parts = ['[A] Accept', '[R] Reject', '[T] Toggle']
        if ((total || 0) > 1) parts.push('[←→] Navigate')
        parts.push('[X] Cancel')
        const controls = parts.join('  ')
        const header = `${kind} — ${(index ?? 0) + 1} of ${total} · ${progress}`
        const reasonLine = reason ? `"${reason}"` : ''
        const viewLine = `Showing: ${viewLabel}`
        // Block-Delta-Tabelle: zeigt Vorher/Nachher fuer Heading/List/Blockquote,
        // wenn der Vorschlag das Block-Format aendert. Im Composite ist das
        // ohnehin per Toggle sichtbar (WYSIWYG), aber das Panel gibt eine
        // schnelle Zusammenfassung ohne Toggle.
        const blockLines = blockDelta ? this._formatBlockDelta(blockDelta) : []
        return [header, reasonLine, '', ...blockLines, controls, viewLine].filter(l => l !== '').join('\n')
    }

    _formatBlockDelta(delta) {
        const oldLabel = describeBlock(delta.old)
        const newLabel = describeBlock(delta.new)
        if (oldLabel === newLabel) return []
        return [`Block: ${oldLabel} → ${newLabel}`, '']
    }

    /**
     * Fuegt eine System-Log-Meldung hinzu.
     * @param {string} text
     * @param {string} type - 'SYSTEM' (default), 'ERROR', etc.
     */
    log(text, type = 'SYSTEM') {
        this.model.addEntry(type, text)
        this.view.rebuildLines()
        this.view.scrollToBottom()
        if (type === 'ERROR') this._notifyErrorEntry()
    }

    /**
     * Meldet eine neue ERROR-Zeile nach aussen — main.js klappt darauf die
     * eingeklappte Console auf, damit Fehler (Diktat, Agent, Rate-Limit)
     * nicht unsichtbar verhallen (Testphasen-Entscheidung 2026-07-02).
     * Der Hook darf den Log-Pfad nie brechen.
     */
    _notifyErrorEntry() {
        if (!this.onErrorEntry) return
        try { this.onErrorEntry() } catch { /* UI-Hook nie fatal */ }
    }

    // === Event Handling ===

    /**
     * Leitet ein Keyboard-Event weiter.
     * @param {KeyboardEvent} e
     * @returns {boolean}
     */
    handleKeydown(e) {
        return this.input.handleKeydown(e)
    }

    /**
     * Leitet Mouse-Down weiter.
     * @param {number} x - X relativ zum innerBounds
     * @param {number} y - Y relativ zum innerBounds
     * @param {MouseEvent} e
     * @param {number|null} pixelX - Pixel-Position (proportional)
     */
    handleMouseDown(x, y, e, pixelX = null, pixelY = null, fractionalY) {
        // Scrollbar-Klick? (Minimap im Proportional-Mode, Thumb/Track im Monospace)
        if (this.view.isOnScrollbar(x)) {
            this.isScrollbarDragging = true
            const clickY = fractionalY !== undefined ? fractionalY : y
            this._scrollbarDragStartY = clickY
            this._scrollbarDragStartViewport = this.view.viewportStart

            // Klick auf Track (nicht auf Viewport-Handle): direkt zu dieser Position springen
            if (!this.view.isOnScrollbarThumb(y)) {
                this.view.scrollToScrollbarY(clickY)
                this._scrollbarDragStartViewport = this.view.viewportStart
            }
            return
        }

        // Link-Klick: URL im neuen Tab oeffnen
        const link = this.view.getLinkAtPosition(x, y, pixelX, pixelY)
        if (link) {
            window.open(link, '_blank', 'noopener,noreferrer')
            return
        }
        this.input.handleMouseDown(x, y, e.shiftKey, pixelX, pixelY)
    }

    /**
     * @returns {boolean} true, solange ein Output-Drag laeuft. Mirror auf
     *   `CoreInput.isOutputDragging()` — vom globalen mouseMove-Dispatch
     *   genutzt, um Drag-Events auch ausserhalb des Core-Panes durchzureichen.
     */
    isOutputDragging() {
        return this.input.isOutputDragging()
    }

    /**
     * Leitet Mouse-Move weiter.
     * @param {number} x
     * @param {number} y
     * @param {number|null} pixelX - Pixel-Position (proportional)
     * @param {number|null} pixelY - Pixel-Y (für Quarter-Spacing-genaue Hit-Detection)
     * @param {number} [fractionalY] - Fractional Y für pixelgenauen Scrollbar-Drag
     */
    handleMouseMove(x, y, pixelX = null, pixelY = null, fractionalY) {
        if (this.isScrollbarDragging) {
            const currentY = fractionalY !== undefined ? fractionalY : y
            const deltaY = currentY - this._scrollbarDragStartY
            this.view.scrollByThumbDrag(deltaY, this._scrollbarDragStartViewport)
            return
        }
        this.input.handleMouseMove(x, y, pixelX, pixelY)
    }

    /**
     * Leitet Mouse-Up weiter.
     */
    handleMouseUp() {
        if (this.isScrollbarDragging) {
            this.isScrollbarDragging = false
            return
        }
        this.input.handleMouseUp()
    }

    /**
     * Leitet Mouse-Wheel weiter.
     * @param {number} deltaY
     * @param {number} deltaMode
     */
    handleWheel(deltaY, deltaMode) {
        this.input.handleWheel(deltaY, deltaMode)
    }

    // === Rendering ===

    /**
     * Prueft ob die Console neu gerendert werden muss.
     * @returns {boolean}
     */
    needsRender() {
        return this.model.isDirty()
    }

    /**
     * Rendert den Console-Inhalt.
     * @param {number} x - offsetX (innerBounds.x)
     * @param {number} y - offsetY (innerBounds.y)
     * @param {function} setCharFn
     */
    render(x, y, setCharFn) {
        // Lines bei Bedarf neu aufbauen
        const lvl = this.model.getDirtyLevel()
        if (lvl === 'STRUCTURAL') {
            // Collapse/Expand-Toggles wollen die Scroll-Position halten —
            // sonst zieht der Render den Viewport beim Ein-/Ausklappen an
            // das Ende und reisst den User aus dem Kontext.
            const preserveScroll = this.model.consumePreserveScroll()
            this.view.rebuildLines()
            if (preserveScroll) {
                // Viewport neu normalisieren (Zeilen-Anzahl kann sich geaendert
                // haben), aber nicht ans Ende springen.
                this.view.scrollBy(0)
            } else {
                this.view.scrollToBottom()
            }
        } else if (lvl === 'INPUT') {
            this.view.rebuildLines()
            this.view.ensureCursorVisible()
        }
        // SELECTION: kein Rebuild, kein Scroll — nur Re-Render.
        // Output-Selection-Aenderungen (Drag, Klick) duerfen den Viewport
        // nicht ans Ende reissen, sonst springt die Konsole bei jedem
        // mouseMove zum Bottom (siehe CoreModel.setOutputSelection).

        this.view.render(x, y, setCharFn)
        this.model.clearDirty()
    }

    /**
     * Rendert die Scrollbar.
     * @param {number} x
     * @param {number} y
     * @param {number} height
     * @param {function} setCharFn
     */
    renderScrollbar(x, y, height, setCharFn) {
        this.view.renderScrollbar(x, y, height, setCharFn)
    }

    // === Lifecycle ===

    /**
     * Aktualisiert die Pane-Groesse.
     * @param {{x: number, y: number, width: number, height: number}} bounds
     */
    updateBounds(bounds) {
        const changed = this.bounds.width !== bounds.width || this.bounds.height !== bounds.height
        this.bounds = { ...bounds }
        if (changed) {
            this.view.updateBounds(bounds)
        }
    }

    /**
     * Gibt die Cursor-Position fuer das Framework zurueck.
     * @returns {{x: number, y: number}} Screen-Position relativ zum Pane
     */
    getCursorPosition() {
        if (this.model.isLocked()) {
            return { x: -1, y: -1 }  // Kein Cursor im Lock-Zustand
        }
        const pos = this.view.inputToScreen(this.model.getInputCursor())
        if (!pos) return { x: -1, y: -1 }
        return pos
    }

    /** @returns {boolean} Ob die Console gesperrt ist */
    isLocked() {
        return this.model.isLocked()
    }

    /**
     * Setzt den Callback fuer blockierte Aktionen.
     * @param {function} fn
     */
    setActionBlockedCallback(fn) {
        this.input.setActionBlockedCallback(fn)
    }

    /**
     * Setzt den Callback, der aufgerufen wird wenn der Agent-Busy-Status
     * wechselt. Der Empfaenger (typischerweise layout.isAgentBusy) steuert
     * damit die Sichtbarkeit des (!) Abort-Controls.
     * @param {(busy: boolean) => void} fn
     */
    setOnBusyChange(fn) {
        this._onBusyChange = fn
    }

    /**
     * Setzt den Busy-Status fuer ein bestimmtes Projekt und meldet an den
     * Callback — aber nur wenn das angegebene Projekt aktuell aktiv ist.
     * @param {boolean} busy
     * @param {string|null} projectId - Projekt des Streams. Default: aktives Projekt.
     */
    _setBusy(busy, projectId = null) {
        const pid = projectId || this._currentProjectId
        if (pid) this._busyByProject.set(pid, busy)
        if (pid === this._currentProjectId && this._onBusyChange) {
            this._onBusyChange(busy)
        }
    }

    /**
     * Wechselt das aktive Projekt der Console. Aktuelle Entries werden unter
     * `oldProjectId` eingelagert, neue Entries (oder leer) fuer `newProjectId`
     * geladen. Der AgentService bekommt den Projekt-Swap mitgeteilt (sessionId).
     * Laufende Streams bleiben in ihrem Ursprungsprojekt — die Callbacks
     * schreiben per Closure in das zugehoerige (eingelagerte) Entries-Array.
     *
     * @param {string} newProjectId
     * @param {string} [newProjectTitle] - fuer "Active Project"-Meldung
     * @returns {Promise<void>}
     */
    async switchProject(newProjectId, newProjectTitle = '') {
        const oldProjectId = this._currentProjectId
        if (oldProjectId === newProjectId) return

        // Aktuellen Zustand einlagern — als LIVE-Referenz, kein Slice.
        // Laufende Streams halten die Entries-Array-Referenz per Closure/Slot
        // und schreiben weiter. Wuerden wir hier slicen, triftet Model-Array
        // und Slot-Array auseinander, Stream-Chunks gehen bei Rueckkehr verloren.
        // Wenn der Slot bereits existiert (z.B. von einem laufenden Stream
        // angelegt), ist die Array-Referenz schon stabil — wir aktualisieren
        // dann nur nextId.
        if (oldProjectId) {
            const existingSlot = this._projectStates.get(oldProjectId)
            if (existingSlot) {
                existingSlot.nextId = this.model.getNextId()
            } else {
                this._projectStates.set(oldProjectId, {
                    entries: this.model.getEntries(),
                    nextId: this.model.getNextId()
                })
            }
        }

        // Neuen Zustand laden. Beim allerersten Projekt-Wechsel (oldProjectId=null)
        // bleibt der aktuelle Model-State bestehen — so ueberlebt die Greeting
        // aus initialize() und landet im ersten angezeigten Projekt. Erst ab dem
        // zweiten Wechsel setzen wir leere Entries fuer unbekannte Projekte.
        const newState = this._projectStates.get(newProjectId)
        if (newState) {
            this.model.setEntries(newState.entries, newState.nextId)
        } else if (oldProjectId !== null) {
            // Frisches Projekt: Slot direkt anlegen, damit spaetere Streams
            // eine stabile Referenz vorfinden und Projekt-Wechsel konsistent
            // bleiben.
            const freshEntries = []
            this._projectStates.set(newProjectId, { entries: freshEntries, nextId: 1 })
            this.model.setEntries(freshEntries, 1)
        }

        this._currentProjectId = newProjectId

        // AgentService-Session pro Projekt (beim Erst-Touch verwirft dies die
        // Server-Session, damit Client und Server konsistent sind — wichtig
        // nach Browser-Reload)
        if (this.agentService && typeof this.agentService.switchProject === 'function') {
            await this.agentService.switchProject(newProjectId)
        }

        // "Active Project: NAME" — nur wenn nicht busy auf dem neuen Projekt
        // und letzter Entry nicht bereits dieselbe Meldung ist
        const isNewBusy = this._busyByProject.get(newProjectId) || false
        if (!isNewBusy && newProjectTitle) {
            const msg = `Active Project: ${newProjectTitle}`
            const entries = this.model.getEntries()
            const last = entries.length > 0 ? entries[entries.length - 1] : null
            const duplicate = last && last.type === 'SYSTEM' && last.text === msg
            if (!duplicate) {
                this.model.addEntry('SYSTEM', msg)
            }
        }

        this.view.rebuildLines()
        this.view.scrollToBottom()

        // Busy-Anzeige fuer das neue Projekt setzen
        if (this._onBusyChange) {
            this._onBusyChange(isNewBusy)
        }
    }

    // === Internes ===

    /**
     * Bricht den laufenden Stream ab.
     * Haengt '[abgebrochen]' an den letzten Entry an.
     */
    _handleAbort() {
        // Abort gilt nur dem aktuellen Projekt (User sieht die Konsole des
        // aktiven Projekts). Laufende Streams anderer Projekte bleiben unberuehrt.
        if (!this.model.isLocked()) return
        this.agentService.abort()
        // "Recherchiere..."-Entry entfernen falls vorhanden
        if (this._searchingEntryId) {
            this.model.removeEntry(this._searchingEntryId)
            this._searchingEntryId = null
        }
        // '[aborted]' an den letzten Entry anhaengen (AI oder THINKING).
        // Flag setzen, damit das spaetere onAborted-SSE-Event nicht
        // nochmal ' [aborted]' anhaengt — der User hat den Marker schon
        // gesehen, sobald er (!) gedrueckt hat.
        const entries = this.model.getEntries()
        if (entries.length > 0) {
            const last = entries[entries.length - 1]
            if (last.type === 'AI' || last.type === 'THINKING') {
                this.model.appendToEntry(last.id, ' [aborted]')
            }
        }
        this._abortMarkerWritten = true
        this.model.unlock()
        this.view.rebuildLines()
        this.view.scrollToBottom()
        this._setBusy(false, this._currentProjectId)
        this._playAbortedSound()
    }

    /**
     * Markiert ein geladenes Mock-Scenario als "armed". Der naechste Enter
     * startet es — statt eines Chat-Requests laeuft dann MockAgentTransport.
     * @param {string} scenarioName
     */
    _setMockScenarioPending(scenarioName) {
        this._mockScenarioPending = scenarioName
    }

    /** Loescht die Armierung. */
    _clearMockScenarioPending() {
        this._mockScenarioPending = null
    }

    /**
     * Wird aufgerufen wenn User Enter drueckt.
     * Streaming: Thinking und AI-Antwort bauen sich live auf.
     * @param {string} text - Abgesendeter Text
     */
    async _handleSend(text) {
        // 1. Slash-Kommandos abfangen (z.B. /mock list, /mock <name>).
        //    Der Dispatcher schreibt selber in die Console und signalisiert via
        //    consumeInput, dass der Text nicht als USER-Entry bzw. Chat-Request
        //    weiterlaufen soll. Der USER-Entry wurde vom CoreInput bereits
        //    hinzugefuegt — wir entfernen ihn fuer konsumierte Kommandos, damit
        //    die Console nicht mit Slash-Eingaben zugemuellt wird.
        if (text.trim().startsWith('/')) {
            const res = this._commandDispatcher.handle(text)
            if (res.handled) {
                if (res.consumeInput !== false) {
                    this._removeLastUserEntryIfMatches(text)
                }
                this.view.rebuildLines()
                this.view.scrollToBottom()
                return
            }
        }

        // 2. Armiertes Mock-Scenario? Dann Mock-Pfad statt Chat.
        if (this._mockScenarioPending && this._mockTransport?.hasScenario()) {
            const scenarioName = this._mockScenarioPending
            this._mockScenarioPending = null
            await this._handleMockSend(text, scenarioName)
            return
        }

        // 3. Normaler Chat-Pfad.
        await this._handleChatSend(text)
    }

    /**
     * Holt (oder erstellt) den Projekt-Slot fuer Stream-Entries. Wichtig: wenn
     * der Slot bereits existierte, kann zwischenzeitlich `model.addEntry`
     * direkt aufgerufen worden sein (z.B. `console.log(...)` oder
     * `switchProject` mit "Active Project: ..."). Das bumpt `model.nextId`,
     * aber nicht `slot.nextId` — der Slot driftet hinter dem Model her.
     *
     * Ohne Re-Sync vergibt der Stream-Pfad dann IDs, die es bereits gibt;
     * spaetere `appendToEntry(entryId, ...)` finden via Array-Suche den
     * falschen (alten) Entry, und Stream-Text laeuft in eine voellig andere
     * Console-Zeile hinein.
     *
     * Darum: beim Slot-Zugriff immer `slot.nextId` ggf. auf `model.nextId`
     * hochziehen. Das ist idempotent und hat keinen Effekt, wenn die beiden
     * bereits synchron sind.
     * @returns {{entries: ConsoleEntry[], nextId: number}}
     */
    _getOrCreateProjectSlot(projectId) {
        let slot = this._projectStates.get(projectId)
        if (!slot) {
            slot = {
                entries: this.model.getEntries(),
                nextId: this.model.getNextId()
            }
            this._projectStates.set(projectId, slot)
        } else if (this.model.getNextId() > slot.nextId) {
            slot.nextId = this.model.getNextId()
        }
        return slot
    }

    /** Entfernt den letzten USER-Entry, wenn er dem uebergebenen Text entspricht. */
    _removeLastUserEntryIfMatches(text) {
        const entries = this.model.getEntries()
        if (entries.length === 0) return
        const last = entries[entries.length - 1]
        if (last.type === 'USER' && last.text === text) {
            this.model.removeEntry(last.id)
        }
    }

    /**
     * Erzeugt die gemeinsamen Stream-Hooks (Entry-CRUD, Rebuild, THINKING-
     * Block-State) fuer Mock- und Real-Send-Pfade. Beide Pfade hatten
     * vorher dieselben Closure-Helper — der Refactor entfernt die
     * Duplikation.
     *
     * Closure-State `state.thinkingEntryId` und `state.aiEntryId` wird
     * durch die THINKING-Block-Helper gepflegt; die Send-Pfade lesen
     * sie nur lesen, um Lifecycle-Events (collapse on done) auszuloesen.
     *
     * @param {string|null} streamProjectId Projekt, in das geschrieben wird.
     * @param {{entries: ConsoleEntry[], nextId: number}} slot Project-Slot.
     * @returns {{
     *   state: {thinkingEntryId: number|null, aiEntryId: number|null},
     *   streamEntries: ConsoleEntry[],
     *   addEntry: (type: string, txt: string) => ConsoleEntry,
     *   appendToEntry: (id: number, txt: string) => void,
     *   removeEntryLocal: (id: number) => void,
     *   setEntryCollapsedLocal: (id: number, collapsed: boolean) => void,
     *   scheduleRebuild: () => void,
     *   rebuildAndScroll: () => void,
     *   ensureThinkingEntry: () => number,
     *   appendToThinking: (txt: string) => void,
     *   closeAiBlockBeforeTool: () => void
     * }}
     */
    _makeStreamHooks(streamProjectId, slot) {
        const streamEntries = slot.entries
        const isLive = () => streamProjectId === this._currentProjectId

        const addEntry = (type, txt) => {
            const entry = new ConsoleEntry(slot.nextId++, type, txt)
            streamEntries.push(entry)
            if (isLive()) {
                this.model.nextId = slot.nextId
                this.model._markDirty(DirtyLevel.STRUCTURAL)
                if (type === 'ERROR') this._notifyErrorEntry()
            }
            return entry
        }
        const appendToEntry = (entryId, txt) => {
            const entry = streamEntries.find(e => e.id === entryId)
            if (entry) {
                entry.text += txt
                if (isLive()) this.model._markDirty(DirtyLevel.STRUCTURAL)
            }
        }
        const removeEntryLocal = (entryId) => {
            const idx = streamEntries.findIndex(e => e.id === entryId)
            if (idx !== -1) {
                streamEntries.splice(idx, 1)
                if (isLive()) this.model._markDirty(DirtyLevel.STRUCTURAL)
            }
        }
        const setEntryCollapsedLocal = (entryId, collapsed) => {
            const entry = streamEntries.find(e => e.id === entryId)
            if (entry && entry.collapsed !== collapsed) {
                entry.collapsed = collapsed
                if (isLive()) this.model._markDirty(DirtyLevel.STRUCTURAL)
            }
        }
        const scheduleRebuild = () => {
            if (isLive()) this._scheduleStreamRebuild()
        }
        const rebuildAndScroll = () => {
            if (isLive()) {
                this.view.rebuildLines()
                this.view.scrollToBottom()
            }
        }

        // THINKING-Block-State (gepflegt durch ensure/append/move).
        // firstToolCallSeen: nur die Pre-Tool-Vorrede des Modells gehoert in
        // den Thinking-Block. Spaeterer AI-Text (zwischen Tool-Calls oder nach
        // dem letzten) ist Teil der echten Antwort und bleibt User-facing.
        const state = { thinkingEntryId: null, aiEntryId: null, firstToolCallSeen: false }

        const ensureThinkingEntry = () => {
            if (state.thinkingEntryId) return state.thinkingEntryId
            const entry = addEntry('THINKING', '')
            state.thinkingEntryId = entry.id
            return entry.id
        }
        const appendToThinking = (txt) => {
            ensureThinkingEntry()
            appendToEntry(state.thinkingEntryId, txt)
        }
        // Wird vor jedem tool_use aufgerufen. Schliesst den aktuellen AI-Block
        // ab, damit Tool-Aktivitaet (THINKING) optisch dazwischen liegt und
        // die naechste Antwort einen frischen AI-Block bekommt.
        //
        // Vor dem ERSTEN Tool-Call: aiEntryId resetten — Pre-Tool-Text wie
        // "Klar, schaue ich mir kurz an!" bleibt User-facing stehen (anders
        // als frueher, wo er nach THINKING wanderte). Echtes extended_thinking
        // kommt ueber den separaten onThinking-Pfad in den THINKING-Block.
        //
        // Bei FOLGE-Tool-Calls: aiEntryId behalten, damit zusammenhaengender
        // Text wie "Du steckst..." + "Erledigt..." durchgehend gerendert wird —
        // ABER wir trennen die Stuecke durch einen Newline, sonst klebt
        // "...Review:" und "Der Fix..." aneinander, weil der Server den Stream
        // einfach an der Token-Grenze unterbricht.
        const closeAiBlockBeforeTool = () => {
            if (!state.firstToolCallSeen) {
                state.aiEntryId = null
            } else if (state.aiEntryId) {
                const entry = streamEntries.find(e => e.id === state.aiEntryId)
                if (entry && entry.text && !entry.text.endsWith('\n')) {
                    appendToEntry(state.aiEntryId, '\n')
                }
            }
            state.firstToolCallSeen = true
        }

        // Aktiven Stream registrieren. Wird von logToolStatus/logToolError
        // (THINKING-Block-Pfad, Lesson 81) und logSystem/logError (Slot-Pfad,
        // ID-Sync, Lesson 81-Reprise) genutzt — beide schreiben damit ueber
        // den Stream-Lifecycle statt direkt am Model. Ohne den Slot-Pfad
        // kollidieren Hook-IDs mit Direct-Add-IDs, weil `slot.nextId` und
        // `model.nextId` auseinanderlaufen.
        this._activeStream = { state, appendToThinking, addEntry, scheduleRebuild }

        return {
            state, streamEntries,
            addEntry, appendToEntry, removeEntryLocal, setEntryCollapsedLocal,
            scheduleRebuild, rebuildAndScroll,
            ensureThinkingEntry, appendToThinking, closeAiBlockBeforeTool
        }
    }

    /**
     * Baut den `finish()`-Callback fuer einen Stream-Pfad. Loest den Lock
     * (sofern kein Review aktiv ist), rebuildet, setzt Busy zurueck.
     * Optionaler `beforeUnlock`-Hook fuer Pfad-spezifisches Cleanup
     * (z.B. searching-Entry im Real-Pfad).
     *
     * @param {string|null} streamProjectId
     * @param {ReviewController|null} rc
     * @param {{rebuildAndScroll: () => void}} hooks
     * @param {{beforeUnlock?: () => void}} [extra]
     * @returns {() => void}
     */
    _makeFinishHandler(streamProjectId, rc, hooks, extra = {}) {
        return () => {
            if (extra.beforeUnlock) extra.beforeUnlock()
            const reviewActive = rc?.isActive
            if (streamProjectId === this._currentProjectId && !reviewActive) {
                this.model.unlock()
            }
            hooks.rebuildAndScroll()
            this._setBusy(false, streamProjectId)
            // Stream-Lifecycle vorbei — ToolStatus-Logs ab jetzt wieder als
            // SYSTEM-Entry (es gibt keinen aktiven THINKING-Block mehr).
            this._activeStream = null
        }
    }

    /**
     * Baut die Phase-3-Auto-Apply-Handler aus der deklarativen Tool-Map
     * (Review v1.4 #5). Mock- und Real-Pfad teilen sich diese Logik 1:1 —
     * wenn ein ReviewController da ist, gehen die Mutationen direkt ans
     * Modell + reportActionDone. Ohne RC landet nur ein SYSTEM-Log in der
     * Console (reiner Test-Pfad).
     *
     * Neue Tools (z.B. Phase 4) ergaenzen einen Eintrag in
     * js/agent/autoApplyToolMap.js — Handler entstehen automatisch.
     *
     * @param {ReviewController|null} rc
     * @param {(type: string, txt: string) => void} addEntry
     * @param {() => void} scheduleRebuild
     * @returns {object} Handler-Objekt zum Spreaden in den callbacks
     */
    _buildAutoApplyHandlers(rc, addEntry, scheduleRebuild) {
        const handlers = {}
        for (const spec of AUTO_APPLY_TOOLS) {
            handlers[`onPropose${spec.handlerName}`] = (payload) => {
                if (rc) {
                    rc[`apply${spec.handlerName}`](payload)
                } else {
                    addEntry('SYSTEM', `[${spec.event}] ${payload.toolCallId}`)
                }
                scheduleRebuild()
            }
        }
        return handlers
    }

    /**
     * Laeuft fuer /mock-armierte Nachrichten: ruft den MockAgentTransport auf
     * und schreibt die Events in die Console. Entry-Mechanismen wie im
     * Chat-Pfad (pro-Projekt Slot, Busy-Flag). Propose-Events werden in
     * Phase A nur als SYSTEM-Entries geloggt — erst in Phase B greift der
     * ReviewController hier zu.
     * @param {string} text
     * @param {string} scenarioName
     */
    async _handleMockSend(text, scenarioName) {
        const streamProjectId = this._currentProjectId
        const slot = this._getOrCreateProjectSlot(streamProjectId)
        const hooks = this._makeStreamHooks(streamProjectId, slot)
        const {
            state, addEntry, appendToEntry, setEntryCollapsedLocal,
            scheduleRebuild, appendToThinking, closeAiBlockBeforeTool
        } = hooks

        addEntry('SYSTEM', `[mock: ${scenarioName}]`)

        this.model.lock()
        this._setBusy(true, streamProjectId)
        this._streamRebuildPending = false

        const rc = this._reviewController   // kann null sein
        const finish = this._makeFinishHandler(streamProjectId, rc, hooks)

        const cursorContext = this._cursorContextProvider
            ? this._cursorContextProvider()
            : null

        try {
            await this._mockTransport.sendMessage(text, { projectId: streamProjectId, cursorContext }, {
                onSession: () => { /* no-op in Phase A */ },
                onThinking: ({ text: chunk }) => {
                    appendToThinking(chunk)
                    scheduleRebuild()
                },
                onThinkingDone: () => {
                    // Bewusst KEIN collapse hier — wir lassen den Thinking-
                    // Block offen, bis Tools+Iterations vorbei sind und
                    // onDone collapst.
                },
                onDelta: ({ text: chunk }) => {
                    if (!state.aiEntryId) {
                        const entry = addEntry('AI', chunk)
                        state.aiEntryId = entry.id
                    } else {
                        appendToEntry(state.aiEntryId, chunk)
                    }
                    scheduleRebuild()
                },
                onToolCall: ({ tool, input }) => {
                    closeAiBlockBeforeTool()
                    appendToThinking(formatToolCall(tool, input))
                    scheduleRebuild()
                },
                onToolResult: ({ tool, summary }) => {
                    appendToThinking(formatToolResult(tool, summary))
                    scheduleRebuild()
                },
                onProposeRewrite: (payload) => {
                    if (rc) rc.onProposeRewrite(payload)
                    else addEntry('SYSTEM', `[propose_rewrite] ${payload.toolCallId}`)
                    scheduleRebuild()
                },
                onProposeInsert: (payload) => {
                    if (rc) rc.onProposeInsert(payload)
                    else addEntry('SYSTEM', `[propose_insert] ${payload.toolCallId}`)
                    scheduleRebuild()
                },
                onProposeDelete: (payload) => {
                    if (rc) rc.onProposeDelete(payload)
                    else addEntry('SYSTEM', `[propose_delete] ${payload.toolCallId}`)
                    scheduleRebuild()
                },
                // Phase-3+4 Auto-Apply: gemeinsamer Helper fuer Mock- und
                // Real-Pfad. Erzeugt alle onProposeXxx-Handler aus
                // AUTO_APPLY_TOOLS deklarativ — Phase-4-Tools sind hier
                // automatisch dabei (UpdateUserMemory, UpdateContextInfo,
                // JumpToSection).
                ...this._buildAutoApplyHandlers(rc, addEntry, scheduleRebuild),
                onRateLimit: ({ retryAfter }) => {
                    addEntry('ERROR', `Rate limit — retry in ${retryAfter}s`)
                    scheduleRebuild()
                },
                onDone: () => {
                    // ReviewController flushed den Puffer — wenn er Propose-
                    // Events gesammelt hat, startet er jetzt den Review und
                    // haelt den Lock. finish() unlockt dann nicht.
                    if (state.thinkingEntryId) setEntryCollapsedLocal(state.thinkingEntryId, true)
                    if (rc) rc.onDone()
                    finish()
                    this._playReadySound()
                    // Fuer den naechsten done-Event im gleichen Scenario
                    // (reaktive Phase): neuen Block erlauben.
                    state.thinkingEntryId = null
                    state.aiEntryId = null
                    state.firstToolCallSeen = false
                },
                onAborted: () => {
                    if (state.thinkingEntryId) setEntryCollapsedLocal(state.thinkingEntryId, true)
                    addEntry('SYSTEM', '[aborted]')
                    if (rc) rc.onAborted()
                    finish()
                    this._playAbortedSound()
                },
                onError: (error) => {
                    if (state.thinkingEntryId) setEntryCollapsedLocal(state.thinkingEntryId, true)
                    addEntry('ERROR', error.message)
                    finish()
                }
            })
        } catch (err) {
            if (err?.name !== 'AbortError') {
                addEntry('ERROR', `Mock error: ${err.message}`)
            }
            finish()
        }
    }

    async _handleChatSend(text) {
        // Stream-Kontext an den Projekt-Slot binden: beim Projekt-Wechsel
        // waehrend des Streams bleibt die Slot-Referenz stabil, und die
        // Callbacks schreiben weiter in das Entries-Array des Ursprungs-
        // projekts (wo es der User beim Zurueckkehren wiederfindet).
        // Der Busy-Status wird ebenfalls dort freigegeben — nicht im
        // gerade aktiven Projekt.
        const streamProjectId = this._currentProjectId

        // Slot sicherstellen: wenn noch keiner existiert, aktuellen
        // Model-State uebernehmen. Ab jetzt ist die Array-Referenz
        // stabil — switchProject slict sie nicht mehr weg.
        const slot = this._getOrCreateProjectSlot(streamProjectId)
        const hooks = this._makeStreamHooks(streamProjectId, slot)
        const {
            state, addEntry, appendToEntry, removeEntryLocal, setEntryCollapsedLocal,
            scheduleRebuild, appendToThinking, closeAiBlockBeforeTool
        } = hooks

        // Lock wird nur auf dem aktuellen Model gesetzt — fuer weiterlaufende
        // Streams anderer Projekte ist "lock" ein View-Konzept der Console.
        // Beim Projektwechsel wird der Lock vom Swap nicht erhalten. Das ist
        // OK: der Stream laeuft im Hintergrund weiter; wenn der User zurueck
        // kehrt und die Konsole wieder anzeigt, wird der Lock ueber das
        // busy-Flag des Projekts wieder signalisiert.
        this.model.lock()
        this._setBusy(true, streamProjectId)
        this._streamRebuildPending = false

        let searchingEntryId = null
        this._searchingEntryId = null
        // Reset bei jedem neuen Stream — der naechste Abort soll wieder einen
        // Marker schreiben, falls user-initiiert.
        this._abortMarkerWritten = false
        const rc = this._reviewController   // kann null sein

        // Searching-Entry raeumen (gemeinsam in onDelta + finish-Pfaden).
        const clearSearchingEntry = () => {
            if (searchingEntryId) {
                removeEntryLocal(searchingEntryId)
                searchingEntryId = null
                if (streamProjectId === this._currentProjectId) {
                    this._searchingEntryId = null
                }
            }
        }

        // Gemeinsamer Cleanup fuer alle Beendigungspfade. Searching-Entry
        // wird vor dem Lock-Release weggeraeumt, weil er sonst sichtbar
        // bliebe.
        const finish = this._makeFinishHandler(streamProjectId, rc, hooks, {
            beforeUnlock: clearSearchingEntry
        })

        try {
            await this.agentService.sendStreaming(text, {
                onThinking: (chunk) => {
                    // chunk kommt als String oder { text } — beide Pfade
                    // stuetzen. RealAgentTransport reicht das SSE-data
                    // unveraendert weiter ({text}); AgentService normiert.
                    const text = typeof chunk === 'string' ? chunk : chunk?.text || ''
                    appendToThinking(text)
                    scheduleRebuild()
                },
                onThinkingDone: () => {
                    // Wir lassen den Thinking-Block bewusst offen, bis der
                    // gesamte Loop fertig ist — sonst klappt er zwischen
                    // Iterationen mehrfach auf/zu. onDone collapst final.
                },
                onSearching: () => {
                    const entry = addEntry('SYSTEM', 'Recherchiere...')
                    searchingEntryId = entry.id
                    if (streamProjectId === this._currentProjectId) {
                        this._searchingEntryId = entry.id
                    }
                    scheduleRebuild()
                },
                onDelta: (chunk) => {
                    clearSearchingEntry()
                    const text = typeof chunk === 'string' ? chunk : chunk?.text || ''
                    if (!state.aiEntryId) {
                        const entry = addEntry('AI', text)
                        state.aiEntryId = entry.id
                    } else {
                        appendToEntry(state.aiEntryId, text)
                    }
                    scheduleRebuild()
                },
                onToolCall: ({ tool, input }) => {
                    closeAiBlockBeforeTool()
                    appendToThinking(formatToolCall(tool, input))
                    scheduleRebuild()
                },
                onToolResult: ({ tool, summary }) => {
                    appendToThinking(formatToolResult(tool, summary))
                    scheduleRebuild()
                },
                onProposeRewrite: (payload) => {
                    if (rc) rc.onProposeRewrite(payload)
                    else addEntry('SYSTEM', `[propose_rewrite] ${payload.toolCallId}`)
                    scheduleRebuild()
                },
                onProposeInsert: (payload) => {
                    if (rc) rc.onProposeInsert(payload)
                    else addEntry('SYSTEM', `[propose_insert] ${payload.toolCallId}`)
                    scheduleRebuild()
                },
                onProposeDelete: (payload) => {
                    if (rc) rc.onProposeDelete(payload)
                    else addEntry('SYSTEM', `[propose_delete] ${payload.toolCallId}`)
                    scheduleRebuild()
                },
                // Phase-3+4 Auto-Apply: gemeinsamer Helper fuer Mock- und
                // Real-Pfad. Erzeugt alle onProposeXxx-Handler aus
                // AUTO_APPLY_TOOLS deklarativ — Phase-4-Tools sind hier
                // automatisch dabei (UpdateUserMemory, UpdateContextInfo,
                // JumpToSection).
                ...this._buildAutoApplyHandlers(rc, addEntry, scheduleRebuild),
                onRateLimit: ({ retryAfter } = {}) => {
                    const wait = retryAfter ? `retry in ${retryAfter}s` : 'try again later'
                    addEntry('ERROR', `Rate limit hit — ${wait}`)
                    finish()
                },
                onIterationLimit: () => {
                    appendToThinking('\n[iteration_limit reached — wrapping up]\n')
                    scheduleRebuild()
                    // Recap-Stream laeuft danach noch durch (deltas folgen);
                    // unlock erfolgt erst im onDone.
                },
                onModelFallback: ({ to } = {}) => {
                    // Fable 5 nicht erreichbar — Server ist transparent auf das
                    // Fallback-Modell umgeschaltet. Kurze Notiz, damit der Autor
                    // den Modell-/Kostenwechsel kennt.
                    const label = to === 'claude-opus-4-8' ? 'Opus 4.8' : (to || 'fallback model')
                    addEntry('SYSTEM', `Fable 5 unavailable — switched to ${label} for this session.`)
                    scheduleRebuild()
                },
                onSessionRecovered: () => {
                    // Die Server-Session war abgelaufen; der Server hat sie
                    // transparent neu angelegt und faehrt mit dem Prompt fort.
                    // Kurze Notiz, damit der Autor weiss, dass der Agent den
                    // bisherigen Gespraechsverlauf nicht mehr kennt.
                    addEntry('SYSTEM', 'Conversation context expired — continuing in a fresh session.')
                    scheduleRebuild()
                },
                // Phase 4.5: await_review-Lifecycle. Server pausiert auf dem
                // Tool, der Stream bleibt offen — wir markieren den Status im
                // Thinking-Block, damit der User sieht, dass der Agent nicht
                // weg ist sondern aktiv auf seine Reviews wartet.
                onAwaitReviewStarted: ({ expecting, summary }) => {
                    const n = expecting === 1 ? '1 suggestion' : `${expecting || 0} suggestions`
                    const tail = summary ? ` — ${summary}` : ''
                    appendToThinking(`\n[awaiting your review of ${n}${tail}]\n`)
                    scheduleRebuild()
                    // ReviewController von COLLECTING in REVIEWING flushen,
                    // sonst greifen A/R/T-Tasten nicht und der Vorschlag wird
                    // nie ueberlagert dargestellt (bei await_review fehlt das
                    // sonst flushende `done`-SSE-Event).
                    if (rc) rc.onAwaitReviewStarted?.()
                },
                onAwaitReviewDone: ({ status, stillPending }) => {
                    if (status === 'timeout') {
                        const n = (stillPending || []).length
                        appendToThinking(`\n[review wait timed out — ${n} pending]\n`)
                    } else {
                        appendToThinking(`\n[reviews received — continuing]\n`)
                    }
                    // Turn-Abschnitt schliessen: THINKING collapsen, AI-Block-
                    // Slot leeren. Symmetrisch zum onDone-Pfad — sonst klebt
                    // die Folge-Antwort ("Angenommen!...") direkt an den
                    // Pre-Review-Text und der THINKING-Block bleibt offen.
                    // Reihenfolge: erst appendToThinking (das ensure'd den
                    // Block, falls nicht offen), dann collapsen, dann state
                    // ruecksetzen.
                    if (state.thinkingEntryId) setEntryCollapsedLocal(state.thinkingEntryId, true)
                    state.thinkingEntryId = null
                    state.aiEntryId = null
                    state.firstToolCallSeen = false
                    scheduleRebuild()
                },
                onAborted: () => {
                    // Bei user-initiiertem Abort hat _handleAbort den Marker
                    // bereits geschrieben + Sound gespielt — wir wollen das
                    // nicht doppeln. Bei server-initiiertem Abort
                    // (Iteration-Limit, Disconnect) ist das Flag false und
                    // wir schreiben hier den Marker selber.
                    if (!this._abortMarkerWritten) {
                        if (state.aiEntryId) appendToEntry(state.aiEntryId, ' [aborted]')
                        else addEntry('SYSTEM', '[aborted]')
                        this._playAbortedSound()
                    }
                    if (state.thinkingEntryId) setEntryCollapsedLocal(state.thinkingEntryId, true)
                    if (rc) rc.onAborted?.()
                    finish()
                },
                onDone: (usage) => {
                    if (state.aiEntryId && usage?.citations?.length > 0) {
                        const sources = usage.citations
                            .map(c => `- ${c.title || c.url}: ${c.url}`)
                            .join('\n')
                        appendToEntry(state.aiEntryId, '\n\n---\nSources:\n' + sources)
                    }
                    if (state.thinkingEntryId) setEntryCollapsedLocal(state.thinkingEntryId, true)
                    if (rc) rc.onDone?.()
                    finish()
                    this._playReadySound()
                },
                onError: (error) => {
                    if (state.thinkingEntryId) setEntryCollapsedLocal(state.thinkingEntryId, true)
                    addEntry('ERROR', error.message)
                    finish()
                }
            })
        } catch (err) {
            if (err.name !== 'AbortError') {
                addEntry('ERROR', `Verbindungsfehler: ${err.message}`)
            }
            finish()
        }
    }

    /** Spielt den AI-Ready-Sound (Fallback auf inline Audio wenn kein SoundManager). */
    _playReadySound() {
        if (this._soundManager) {
            this._soundManager.playAIReady()
            return
        }
        try {
            const audio = new Audio('sounds/ai_ready.mp3')
            audio.volume = 0.4
            audio.play().catch(() => { /* Autoplay blockiert — ignorieren */ })
        } catch {
            // Audio nicht verfuegbar — ignorieren
        }
    }

    /** Spielt den AI-Abort-Sound. */
    _playAbortedSound() {
        if (this._soundManager) {
            this._soundManager.playAIAborted()
        }
    }

    /** Throttled Rebuild fuer Streaming — maximal 1x pro Frame */
    _scheduleStreamRebuild() {
        if (!this._streamRebuildPending) {
            this._streamRebuildPending = true
            requestAnimationFrame(() => {
                this.view.rebuildLines()
                this.view.scrollToBottom()
                this._streamRebuildPending = false
            })
        }
    }

    /**
     * Gibt die URL an einer Screen-Position zurueck (fuer Hover-Cursor).
     * @param {number} x - X relativ zum innerBounds
     * @param {number} y - Y relativ zum innerBounds
     * @param {number|null} pixelX
     * @returns {string|null} URL oder null
     */
    getLinkAtPosition(x, y, pixelX = null, pixelY = null) {
        return this.view.getLinkAtPosition(x, y, pixelX, pixelY)
    }

    /** Raeumt Ressourcen auf (Keep-Alive-Timer, AbortController). */
    destroy() {
        if (this.agentService && this.agentService.destroy) {
            this.agentService.destroy()
        }
    }
}
